import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, Paperclip, CheckCircle2, XCircle, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";
import { ReviewStatusBadge, downloadFile } from "@/lib/enquiries";

export default function ReviewerReviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/technical-reviewer/reviews/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex h-64 items-center justify-center" data-testid="review-detail-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const { review, enquiry, supplier_name, response, attachments, buyer_org_name } = data;
  const done = review.status === "approved" || review.status === "rejected";

  const decide = async () => {
    if (confirm === "reject" && !comment.trim()) {
      toast.error("A comment is required when rejecting a quotation");
      setConfirm(null);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/technical-reviewer/reviews/${id}/${confirm === "approve" ? "approve" : "reject"}`, { comment });
      toast.success(confirm === "approve" ? "Quotation approved" : "Quotation rejected");
      setConfirm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="reviewer-review-detail" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="icon" data-testid="review-back-btn" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-heading text-2xl font-semibold tracking-tight font-mono" data-testid="review-enquiry-number">{enquiry.enquiry_number}</h1>
            <ReviewStatusBadge status={review.status} displayStatus={review.display_status} />
          </div>
          <p className="text-sm text-muted-foreground">Seller: <span className="font-medium text-slate-700">{supplier_name}</span> · {buyer_org_name}</p>
        </div>
        <Button variant="outline" data-testid="download-buyer-enquiry-btn" onClick={() => downloadFile(`/technical-reviewer/reviews/${id}/enquiry-pdf`, `${enquiry.enquiry_number}.pdf`)}>
          <Download className="mr-1.5 h-4 w-4" /> Download Buyer Enquiry
        </Button>
      </div>

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Event Information</h2>
        <div className="overflow-x-auto px-5 pt-4">
          <table className="w-full text-sm" data-testid="review-items-table">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4 font-medium">#</th>
                <th className="py-2 pr-4 font-medium">Material Code</th>
                <th className="py-2 pr-4 font-medium">Description</th>
                <th className="py-2 pr-4 font-medium text-right">Qty</th>
                <th className="py-2 font-medium">UOM</th>
              </tr>
            </thead>
            <tbody>
              {(enquiry.items || []).map((it, i) => (
                <tr key={it.id || it.material_code} className="border-b last:border-0" data-testid={`review-item-row-${i}`}>
                  <td className="py-2.5 pr-4 font-mono text-xs text-muted-foreground">{i + 1}</td>
                  <td className="py-2.5 pr-4 font-mono text-xs font-semibold">{it.material_code}</td>
                  <td className="py-2.5 pr-4">{it.description}</td>
                  <td className="py-2.5 pr-4 text-right font-mono">{it.quantity}</td>
                  <td className="py-2.5 font-mono text-xs">{it.uom}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 px-5 py-4 md:grid-cols-4">
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Buyer Organization</p><p className="mt-1 text-sm">{buyer_org_name}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Incoterms</p><p className="mt-1 font-mono text-sm">{enquiry.incoterms}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Required Delivery</p><p className="mt-1 text-sm">{enquiry.delivery_days} days</p></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Review Deadline</p><p className={`mt-1 text-sm font-semibold ${review.overdue ? "text-[#DC2626]" : ""}`} data-testid="review-deadline">{review.deadline ? fmtDateTime(review.deadline) : "—"}</p></div>
        </div>
      </div>

      {review.buyer_comment && (
        <div className="rounded-lg border border-[#BFDBFE] bg-[#DBEAFE]/30 px-5 py-4" data-testid="buyer-comment-card">
          <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-[#1E40AF]"><MessageSquare className="h-3.5 w-3.5" /> Buyer Comment</p>
          <p className="mt-1.5 text-sm">{review.buyer_comment}</p>
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Seller Response — {supplier_name}</h2>
        {response ? (
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 px-5 py-4 md:grid-cols-4" data-testid="seller-technical-response">
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">GST %</p><p className="mt-1 font-mono text-sm font-semibold">{response.gst_pct}%</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Delivery</p><p className="mt-1 font-mono text-sm">{response.delivery_days} days</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Incoterms</p><p className="mt-1 font-mono text-sm">{response.incoterms}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Payment Terms</p><p className="mt-1 text-sm">{response.payment_terms}</p></div>
            <div className="col-span-2 md:col-span-3"><p className="text-xs uppercase tracking-wide text-muted-foreground">Technical Specifications</p><p className="mt-1 text-sm">{response.tech_specs}</p></div>
            {response.comment && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Seller Comments</p><p className="mt-1 text-sm">{response.comment}</p></div>}
          </div>
        ) : (
          <p className="px-5 py-6 text-sm text-muted-foreground">Seller has not submitted a response yet.</p>
        )}
        {attachments.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t px-5 py-3" data-testid="review-attachments">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Attachments</span>
            {attachments.map((f) => (
              <span key={f.id} className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs">
                <Paperclip className="h-3 w-3" /> {f.filename}
                <span className="rounded bg-slate-100 px-1 text-[10px] uppercase text-slate-500">{f.category || "other"}</span>
                <button data-testid={`attachment-download-${f.id.slice(0, 8)}`} className="ml-1 text-blue-700 hover:underline"
                  onClick={() => downloadFile(`/enquiry-files/${f.id}/download`, f.filename)}>Download</button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card" data-testid="technical-evaluation-card">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Technical Evaluation</h2>
        {done ? (
          <div className="px-5 py-4" data-testid="review-result">
            <div className="flex items-center gap-2">
              {review.status === "approved"
                ? <><CheckCircle2 className="h-5 w-5 text-[#166534]" /><span className="font-semibold text-[#166534]">APPROVED</span></>
                : <><XCircle className="h-5 w-5 text-[#991B1B]" /><span className="font-semibold text-[#991B1B]">REJECTED</span></>}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Reviewed {fmtDateTime(review.reviewed_at)}</p>
            {review.reviewer_comment && <p className="mt-2 rounded-md border bg-slate-50 p-3 text-sm" data-testid="reviewer-comment-display">"{review.reviewer_comment}"</p>}
          </div>
        ) : (
          <div className="space-y-4 px-5 py-4">
            <div>
              <Label>Reviewer Comment {`(optional when approving, required when rejecting)`}</Label>
              <Textarea data-testid="reviewer-comment-input" rows={3} className="mt-1.5" placeholder="e.g. Technical specifications meet the required standards / Material grade does not comply…" value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>
            <div className="flex gap-3">
              <Button data-testid="approve-quotation-btn" onClick={() => setConfirm("approve")} className="bg-[#166534] hover:bg-[#14532D]">
                <CheckCircle2 className="mr-1.5 h-4 w-4" /> Approve Quotation
              </Button>
              <Button data-testid="reject-quotation-btn" variant="outline" onClick={() => setConfirm("reject")} className="border-red-200 text-red-700 hover:bg-red-50">
                <XCircle className="mr-1.5 h-4 w-4" /> Reject Quotation
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Review Timeline</h2>
        <div className="divide-y" data-testid="review-timeline">
          <div className="px-5 py-3 text-sm">Assigned by {review.assigned_by} <span className="text-xs text-muted-foreground">· {fmtDateTime(review.assigned_at)}</span></div>
          {review.opened_at && <div className="px-5 py-3 text-sm">Opened by you <span className="text-xs text-muted-foreground">· {fmtDateTime(review.opened_at)}</span></div>}
          {review.reviewed_at && <div className="px-5 py-3 text-sm capitalize">{review.status} <span className="text-xs text-muted-foreground">· {fmtDateTime(review.reviewed_at)}</span></div>}
          {(review.reassignment_history || []).map((h, i) => (
            <div key={`${h.at}-${h.previous_reviewer}-${h.new_reviewer}`} className="px-5 py-3 text-sm" data-testid={`reassignment-${i}`}>
              Reassigned from {h.previous_reviewer} to {h.new_reviewer} by {h.by}
              {h.reason && <span className="text-muted-foreground"> — "{h.reason}"</span>}
              <span className="text-xs text-muted-foreground"> · {fmtDateTime(h.at)}</span>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <DialogContent className="bg-white" data-testid="decision-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{confirm === "approve" ? "Approve Quotation" : "Reject Quotation"}</DialogTitle>
            <DialogDescription>
              {confirm === "approve"
                ? "Are you sure you want to approve this quotation for technical compliance?"
                : "Are you sure you want to reject this quotation? Your comment will be shared with the buyer."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="decision-cancel-btn" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button data-testid="decision-confirm-btn" onClick={decide} disabled={saving}
              className={confirm === "approve" ? "bg-[#166534] hover:bg-[#14532D]" : "bg-red-700 hover:bg-red-800"}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {confirm === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
